/* eslint-disable react-hooks/exhaustive-deps */
// TOPO "Horizonte" (modelo_topo = 11): barra FIXA minimalista do modelo "Horizonte"
// (cinematográfico/editorial). Comportamento:
//  - na HOME, sobre um hero de mídia com véu escuro (14 "Horizonte", 13, 11, 8 e 5) a barra
//    nasce TRANSPARENTE (texto claro); ao rolar > 24px ganha fundo na cor1 translúcida com
//    blur (vidro) e linha fina; nas internas já nasce em vidro e reserva a própria altura;
//  - SOME ao rolar para baixo (depois de 160px) e REAPARECE ao subir — spring do framer-motion
//    (useScroll + useMotionValueEvent, sem listener de scroll cru);
//  - desktop: logo · menu com indicador em PÍLULA que DESLIZA entre os itens (layoutId) e
//    segue o hover (volta ao item da página atual ao sair) · favoritos com contador · CTA
//    botão com o NÚMERO (WhatsApp; sem WhatsApp, ligação). Excedente do menu num painel "Mais";
//  - linha de progresso da leitura (scaleX) na base da barra;
//  - celular: botão que vira "X" e abre um MENU EM TELA CHEIA com fundo desfocado e os links
//    entrando em cascata (stagger), numerados, + contatos e redes. Esc/clique fecham; o foco
//    vai pro 1º link e volta pro botão; a página não rola por baixo.
// TUDO VARIÁVEL: menu/contatos/logo = dados do tenant (mesma origem do topo 10); cores =
// site.corN com contraste calculado. prefers-reduced-motion: sem esconder/deslizar/cascata.
// Default export = ErrorBoundary com fallback pro Topo padrão (molde do TopoAurora).
import { AnimatePresence, motion, MotionConfig, useMotionValueEvent, useScroll, useSpring } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FaFacebook, FaHeart, FaInstagram, FaLinkedin, FaMapMarkerAlt, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, favoritosHref, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useRegiao, useSiteRegiao } from '../../context/Info';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import {
    contatosDoTenant,
    EASE_HORIZONTE,
    hexParaRgba,
    linkWhatsAppDoTenant,
    menuDoTenant,
    mesmaRota,
    redesDoTenant,
    useMovimentoReduzido,
} from '../horizonte/horizonte';
import type { ItemMenu } from '../horizonte/horizonte';
import { temSeletorIdiomas } from '../../../utils/idiomaMoedaSite';
import { IdiomaNavItem } from '../idiomas/menu';
import Topo from '../topo/Topo';
import styles from './TopoHorizonte.module.css';

interface ITopoHorizonte {
    site: any;
    setActiveDrawer: any;
    setBuscaAvancada?: any;
    fallback?: React.ReactNode;
}

// Heros em que a barra pode nascer transparente: mídia em tela cheia com véu escuro no alto.
const HEROS_COM_VEU_ESCURO = new Set<number>([5, 8, 11, 13, 14]);
const ITENS_VISIVEIS = 5;
const ROLAGEM_PARA_VIDRO = 24;
const ROLAGEM_PARA_ESCONDER = 160;

const ICONE_REDE: Record<string, React.ComponentType<{ 'aria-hidden'?: boolean }>> = {
    Instagram: FaInstagram,
    Facebook: FaFacebook,
    Youtube: FaYoutube,
    Tiktok: FaTiktok,
    Linkedin: FaLinkedin,
};

const TopoHorizonteInner: React.FC<ITopoHorizonte> = ({ site: siteProp }) => {
    // MULTI-REGIÃO: contatos (tel1/tel2) trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome, favorito } = useAuth();
    const router = useRouter();
    const codsite = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const reduzido = useMovimentoReduzido();
    // MULTI-FILIAIS (modo popup/tela): a filial ativa vira um botão na barra que abre o seletor
    // do site. Tenant sem regiões (ou no modo barra) ⇒ nomeFilial vazio ⇒ nada muda no markup.
    const { regiaoAtiva, regiaoMatriz, modoPopup, setFilialPopupAberto } = useRegiao();
    const nomeFilial = modoPopup ? regiaoAtiva?.nome || regiaoMatriz?.nome || '' : '';
    const uid = useId().replace(/:/g, '');

    const [isMounted, setIsMounted] = useState(false);
    const [vidro, setVidro] = useState(false);
    const [oculto, setOculto] = useState(false);
    const [maisAberto, setMaisAberto] = useState(false);
    const [menuAberto, setMenuAberto] = useState(false);
    const [hover, setHover] = useState<number | null>(null);
    const maisRef = useRef<HTMLDivElement | null>(null);
    const botaoMenuRef = useRef<HTMLButtonElement | null>(null);
    const primeiroLinkRef = useRef<HTMLAnchorElement | null>(null);

    // ---- rolagem: vidro > 24px; some descendo (> 160px) e volta subindo ----
    const { scrollY, scrollYProgress } = useScroll();
    const progresso = useSpring(scrollYProgress, { stiffness: 200, damping: 34, restDelta: 0.001 });
    useMotionValueEvent(scrollY, 'change', (y) => {
        const anterior = scrollY.getPrevious() ?? 0;
        setVidro(y > ROLAGEM_PARA_VIDRO);
        if (y < ROLAGEM_PARA_ESCONDER || y < anterior) setOculto(false);
        else if (y > anterior + 2) setOculto(true);
    });
    useEffect(() => {
        setIsMounted(true);
        setVidro((window.scrollY || 0) > ROLAGEM_PARA_VIDRO);
    }, []);

    // painel "Mais": fecha no clique fora e no Esc.
    useEffect(() => {
        if (!maisAberto) return;
        const fora = (e: MouseEvent) => {
            if (maisRef.current && !maisRef.current.contains(e.target as Node)) setMaisAberto(false);
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMaisAberto(false);
        };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [maisAberto]);

    // menu em tela cheia: trava a rolagem da página, Esc fecha, foco entra e volta.
    useEffect(() => {
        if (!menuAberto) return;
        const html = document.documentElement;
        const antes = html.style.overflow;
        html.style.overflow = 'hidden';
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuAberto(false);
        };
        document.addEventListener('keydown', esc);
        const t = window.setTimeout(() => primeiroLinkRef.current?.focus(), 120);
        const botao = botaoMenuRef.current;
        return () => {
            html.style.overflow = antes;
            document.removeEventListener('keydown', esc);
            window.clearTimeout(t);
            botao?.focus();
        };
    }, [menuAberto]);
    // troca de rota fecha o menu.
    useEffect(() => {
        const fechar = () => setMenuAberto(false);
        router.events?.on('routeChangeStart', fechar);
        return () => router.events?.off('routeChangeStart', fechar);
    }, [router.events]);

    const withCodsite = (path: string) => (isMounted ? appendCodsiteQuery(path, codsite) : path);

    // Transparente só na home e só sobre hero de véu escuro; nas internas nasce em vidro.
    const sobreHero = router.pathname === '/' && HEROS_COM_VEU_ESCURO.has(Number(site?.modelo_busca));
    const solida = !sobreHero || vidro;
    const escondida = oculto && !menuAberto && !maisAberto && !reduzido;

    // ---- cores: vidro = cor1 translúcida com texto calculado; transparente = texto claro ----
    const corBarra = site?.cor1 || '#0f121a';
    const corBarraTexto = ensureReadableTextColor(isColorLight(corBarra) ? site?.cor11 : site?.cor13, corBarra) || '#ffffff';
    const corCta = site?.cor2 || site?.cor1 || '#c8a46a';
    const corCtaTexto = ensureReadableTextColor(isColorLight(corCta) ? site?.cor11 : site?.cor13, corCta) || '#ffffff';
    // menu em tela cheia: cor1 bem densa; texto calculado sobre ela (a barra fica por cima dele,
    // transparente, então herda essa cor enquanto o menu está aberto).
    const corMenuTexto = corBarraTexto;
    const corTexto = menuAberto ? corMenuTexto : solida ? corBarraTexto : '#ffffff';
    // contador de favoritos e linha de progresso: cor2 se contrastar com a barra.
    const corAcento = ensureReadableTextColor(corCta, solida ? corBarra : '#1c2230', 2.2, corTexto, corTexto) || corTexto;
    const corAcentoTexto = corAcento === corCta ? corCtaTexto : solida ? corBarra : '#1c2230';

    // ---- menu ----
    const navItems = menuDoTenant(site, allDataHome);
    const visiveis = navItems.length > ITENS_VISIVEIS + 1 ? navItems.slice(0, ITENS_VISIVEIS) : navItems;
    const excedentes = navItems.length > ITENS_VISIVEIS + 1 ? navItems.slice(ITENS_VISIVEIS) : [];
    const asPath = router.asPath || '/';
    const ativoIdx = visiveis.findIndex((i) => !i.external && mesmaRota(asPath, i.href));
    const algumExcedenteAtivo = excedentes.some((i) => !i.external && mesmaRota(asPath, i.href));
    // a pílula fica no item em hover; sem hover, no item da página atual (ou no "Mais").
    const maisIdx = visiveis.length;
    const pilulaIdx = hover !== null ? hover : ativoIdx >= 0 ? ativoIdx : algumExcedenteAtivo ? maisIdx : null;

    // ---- contato ----
    const { phones, ctaPhone, ctaIsWhats, ctaHref } = contatosDoTenant(site, 2);
    const ctaNumero = MaskPhone(ctaPhone?.tel || '');
    const redes = redesDoTenant(site);

    const empresa = site?.Dados?.empresa || '';
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const totalFavoritos = isMounted && Array.isArray(favorito) ? favorito.length : 0;

    const transicaoMola = reduzido ? { duration: 0 } : { type: 'spring' as const, stiffness: 420, damping: 36 };

    const pilula = (idx: number) =>
        pilulaIdx === idx ? (
            <motion.span layoutId={`hz-pilula-${uid}`} className={styles.pilula} transition={transicaoMola} aria-hidden="true" />
        ) : null;

    const renderItem = (item: ItemMenu, idx: number) => {
        const ativo = !item.external && mesmaRota(asPath, item.href);
        const cls = `${styles.navLink} ${ativo ? styles.navLinkAtivo : ''}`;
        const comum = {
            className: cls,
            title: item.label,
            onMouseEnter: () => setHover(idx),
            onFocus: () => setHover(idx),
        };
        return item.external ? (
            <a key={idx} href={item.href} rel="noopener noreferrer" {...comum}>
                {pilula(idx)}
                <span className={styles.navTexto}>{item.label}</span>
            </a>
        ) : (
            <Link prefetch={false} key={idx} href={withCodsite(item.href)} aria-current={ativo ? 'page' : undefined} {...comum}>
                {pilula(idx)}
                <span className={styles.navTexto}>{item.label}</span>
            </Link>
        );
    };

    const varsTema = {
        '--hz-texto': corTexto,
        '--hz-barra': corBarra,
        '--hz-vidro': hexParaRgba(corBarra, 0.78),
        '--hz-borda': hexParaRgba(corBarraTexto, 0.12, 'rgba(255, 255, 255, ALPHA)'),
        '--hz-pilula': solida ? hexParaRgba(corBarraTexto, 0.12, 'rgba(255, 255, 255, ALPHA)') : 'rgba(255, 255, 255, 0.16)',
        '--hz-acento': corAcento,
        '--hz-cta': corCta,
        '--hz-cta-texto': corCtaTexto,
        '--hz-menu-fundo': hexParaRgba(corBarra, 0.9),
        '--hz-menu-texto': corMenuTexto,
        '--hz-menu-sutil': hexParaRgba(corMenuTexto, 0.16, 'rgba(255, 255, 255, ALPHA)'),
    } as CSSProperties;

    // ---- variantes do menu em tela cheia ----
    const listaMenu = {
        fechado: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
        aberto: { transition: { staggerChildren: reduzido ? 0 : 0.06, delayChildren: reduzido ? 0 : 0.12 } },
    };
    const itemMenu = {
        fechado: { opacity: 0, y: reduzido ? 0 : 36 },
        aberto: { opacity: 1, y: 0, transition: { duration: reduzido ? 0 : 0.6, ease: EASE_HORIZONTE } },
    };

    return (
        // BUILDER: âncora estável do bloco "topo" (cobre esta variante em qualquer página).
        <MotionConfig reducedMotion="user">
            <div className={styles.wrapper} data-builder-section="topo" style={varsTema}>
                {/* espaçador: só quando a barra nasce em vidro (internas) — reserva a altura dela. */}
                {!sobreHero ? <div className={styles.spacer} aria-hidden="true" /> : null}

                <motion.header
                    className={`${styles.header} ${menuAberto ? styles.comMenu : solida ? styles.solida : styles.transparente}`}
                    initial={false}
                    animate={{ y: escondida ? '-110%' : '0%' }}
                    transition={reduzido ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 34, mass: 0.9 }}
                >
                    <div className={styles.inner}>
                        <Link
                            prefetch={false}
                            href={withCodsite('/')}
                            onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Logo da imobiliária', content_category: 'header_navigation' })}
                            className={styles.logoLink}
                            title={`Imobiliária ${empresa}`}
                        >
                            {logoSrc ? (
                                <Image
                                    src={logoSrc}
                                    alt={`Imobiliária ${empresa}`}
                                    title={`Imobiliária ${empresa}`}
                                    className={styles.logo}
                                    height={48}
                                    width={200}
                                    priority
                                    fetchPriority="high"
                                    unoptimized
                                />
                            ) : (
                                <span className={styles.logoText}>{empresa}</span>
                            )}
                        </Link>

                        {/* menu (desktop): pílula deslizante + painel "Mais" */}
                        <nav
                            className={styles.nav}
                            aria-label="Menu principal"
                            onMouseLeave={() => setHover(null)}
                            onBlur={(e) => {
                                // só solta a pílula quando o foco SAI do menu (não ao passar de um item pro outro).
                                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHover(null);
                            }}
                        >
                            {visiveis.map((item, idx) => renderItem(item, idx))}
                            {excedentes.length > 0 ? (
                                <div
                                    ref={maisRef}
                                    className={styles.mais}
                                    onMouseEnter={() => {
                                        setMaisAberto(true);
                                        setHover(maisIdx);
                                    }}
                                    onMouseLeave={() => setMaisAberto(false)}
                                >
                                    <button
                                        type="button"
                                        className={`${styles.navLink} ${algumExcedenteAtivo ? styles.navLinkAtivo : ''}`}
                                        aria-haspopup="true"
                                        aria-expanded={maisAberto}
                                        onFocus={() => setHover(maisIdx)}
                                        onClick={() => setMaisAberto((v) => !v)}
                                    >
                                        {pilula(maisIdx)}
                                        <span className={styles.navTexto}>
                                            Mais
                                            <motion.svg
                                                viewBox="0 0 24 24"
                                                width="13"
                                                height="13"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                aria-hidden="true"
                                                animate={{ rotate: maisAberto ? 180 : 0 }}
                                                transition={{ duration: 0.25 }}
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </motion.svg>
                                        </span>
                                    </button>
                                    <AnimatePresence>
                                        {maisAberto ? (
                                            <motion.div
                                                className={styles.painel}
                                                role="menu"
                                                initial={{ opacity: 0, y: -8, scale: 0.97 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                                transition={{ duration: 0.22, ease: EASE_HORIZONTE }}
                                            >
                                                {excedentes.map((item, idx) =>
                                                    item.external ? (
                                                        <a key={idx} href={item.href} className={styles.painelLink} rel="noopener noreferrer" role="menuitem" onClick={() => setMaisAberto(false)}>
                                                            {item.label}
                                                        </a>
                                                    ) : (
                                                        <Link
                                                            prefetch={false}
                                                            key={idx}
                                                            href={withCodsite(item.href)}
                                                            className={`${styles.painelLink} ${mesmaRota(asPath, item.href) ? styles.painelLinkAtivo : ''}`}
                                                            role="menuitem"
                                                            onClick={() => setMaisAberto(false)}
                                                        >
                                                            {item.label}
                                                        </Link>
                                                    )
                                                )}
                                            </motion.div>
                                        ) : null}
                                    </AnimatePresence>
                                </div>
                            ) : null}
                        </nav>

                        {/* à direita: filial (multi-filiais) + favoritos + CTA + botão do menu (celular) */}
                        <div className={styles.acoes}>
                            {nomeFilial ? (
                                <button
                                    type="button"
                                    className={styles.filial}
                                    aria-haspopup="dialog"
                                    title="Trocar de filial"
                                    aria-label={`Filial ${nomeFilial} — trocar de filial`}
                                    onClick={() => setFilialPopupAberto(true)}
                                >
                                    <FaMapMarkerAlt className={styles.filialPino} aria-hidden="true" />
                                    <span className={styles.filialRotulo}>Filial</span>
                                    <span className={styles.filialNome}>{nomeFilial}</span>
                                    <svg className={styles.filialChevron} viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
                                        <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            ) : null}
                            {/* 22/10: idioma na pílula do modelo (a da filial) com o suspenso do modelo — rodada 2: fica
                                também com o menu em tela cheia (só a bandeira até 1080px); no celular (≤ 640px, onde a
                                pílula some) entra o compacto abaixo. A linha do menu em tela cheia saiu. */}
                            {temSeletorIdiomas(site) ? (
                                <IdiomaNavItem
                                    ocultarAte={640}
                                    compactoAte={1080}
                                    classeBotao={styles.filial}
                                    classePainel={styles.painel}
                                    classeOpcao={styles.painelLink}
                                    classeOpcaoAtiva={styles.painelLinkAtivo}
                                />
                            ) : null}
                            {temSeletorIdiomas(site) ? <IdiomaNavItem ocultarAcima={640} compacto /> : null}
                            <Link
                                prefetch={false}
                                href={isMounted ? favoritosHref(favorito || [], codsite) : '/imoveis'}
                                className={styles.favoritos}
                                title="Clique aqui para ver seus imóveis favoritos"
                                aria-label={totalFavoritos > 0 ? `Imóveis favoritos (${totalFavoritos})` : 'Imóveis favoritos'}
                            >
                                <FaHeart aria-hidden="true" />
                                <AnimatePresence>
                                    {totalFavoritos > 0 ? (
                                        <motion.span
                                            key={totalFavoritos}
                                            className={styles.badge}
                                            style={{ background: corAcento, color: corAcentoTexto }}
                                            initial={{ scale: 0.4, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            exit={{ scale: 0.4, opacity: 0 }}
                                            transition={{ type: 'spring', stiffness: 520, damping: 22 }}
                                        >
                                            {totalFavoritos}
                                        </motion.span>
                                    ) : null}
                                </AnimatePresence>
                            </Link>

                            {ctaPhone ? (
                                <a
                                    className={styles.cta}
                                    href={ctaHref}
                                    rel="noopener noreferrer"
                                    target={ctaIsWhats ? '_blank' : undefined}
                                    title={ctaPhone.nome || (ctaIsWhats ? `WhatsApp ${ctaNumero}` : `Telefone ${ctaNumero}`)}
                                    aria-label={ctaIsWhats ? `Fale conosco pelo WhatsApp ${ctaNumero}` : `Ligar para ${ctaNumero}`}
                                    onClick={() => {
                                        trackLead(site, null); // whatsapp_click no GTM/GA, como no Topo clássico (sem imóvel = sem Meta)
                                        trackMetaEvent(site, 'Contact', {
                                            content_name: 'Contato do cabeçalho',
                                            contact_method: ctaIsWhats ? 'whatsapp' : 'phone',
                                            content_category: 'header',
                                        });
                                    }}
                                >
                                    {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                    {/* o botão mostra o próprio número (Daniel 14/10, como no modelo 9); sem número formatável, "Fale conosco" */}
                                    <span className={ctaNumero ? `${styles.ctaLabel} ${styles.ctaNumero}` : styles.ctaLabel}>{ctaNumero || 'Fale conosco'}</span>
                                </a>
                            ) : null}

                            <button
                                ref={botaoMenuRef}
                                type="button"
                                className={styles.botaoMenu}
                                aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
                                aria-expanded={menuAberto}
                                aria-controls={`hz-menu-${uid}`}
                                onClick={() => setMenuAberto((v) => !v)}
                            >
                                <motion.span
                                    className={styles.traco}
                                    initial={false}
                                    animate={menuAberto ? { y: 0, rotate: 45 } : { y: -3, rotate: 0 }}
                                    transition={{ duration: reduzido ? 0 : 0.3, ease: EASE_HORIZONTE }}
                                />
                                <motion.span
                                    className={styles.traco}
                                    initial={false}
                                    animate={menuAberto ? { y: 0, rotate: -45, scaleX: 1 } : { y: 3, rotate: 0, scaleX: 0.62 }}
                                    transition={{ duration: reduzido ? 0 : 0.3, ease: EASE_HORIZONTE }}
                                />
                            </button>
                        </div>
                    </div>

                    {/* progresso da leitura da página */}
                    <motion.span className={styles.progresso} style={{ scaleX: progresso }} aria-hidden="true" />
                </motion.header>

                {/* menu em tela cheia (celular/tablet) — FORA do header (que tem transform). */}
                <AnimatePresence>
                    {menuAberto ? (
                        <motion.div
                            id={`hz-menu-${uid}`}
                            className={styles.overlay}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, transition: { duration: 0.25, delay: 0.1 } }}
                            transition={{ duration: reduzido ? 0 : 0.35 }}
                        >
                            <motion.nav
                                className={styles.overlayNav}
                                aria-label="Menu principal"
                                variants={listaMenu}
                                initial="fechado"
                                animate="aberto"
                                exit="fechado"
                            >
                                {navItems.map((item, idx) => {
                                    const ativo = !item.external && mesmaRota(asPath, item.href);
                                    const conteudo = (
                                        <>
                                            <span className={styles.overlayNum} aria-hidden="true">
                                                {String(idx + 1).padStart(2, '0')}
                                            </span>
                                            <span className={styles.overlayTexto}>{item.label}</span>
                                        </>
                                    );
                                    return (
                                        <motion.div key={idx} variants={itemMenu} className={styles.overlayItem}>
                                            {item.external ? (
                                                <a
                                                    ref={idx === 0 ? primeiroLinkRef : undefined}
                                                    href={item.href}
                                                    rel="noopener noreferrer"
                                                    className={styles.overlayLink}
                                                    onClick={() => setMenuAberto(false)}
                                                >
                                                    {conteudo}
                                                </a>
                                            ) : (
                                                <Link
                                                    ref={idx === 0 ? primeiroLinkRef : undefined}
                                                    prefetch={false}
                                                    href={withCodsite(item.href)}
                                                    className={`${styles.overlayLink} ${ativo ? styles.overlayLinkAtivo : ''}`}
                                                    aria-current={ativo ? 'page' : undefined}
                                                    onClick={() => setMenuAberto(false)}
                                                >
                                                    {conteudo}
                                                </Link>
                                            )}
                                        </motion.div>
                                    );
                                })}
                                <motion.div variants={itemMenu} className={styles.overlayItem}>
                                    <Link
                                        prefetch={false}
                                        href={isMounted ? favoritosHref(favorito || [], codsite) : '/imoveis'}
                                        className={styles.overlayLink}
                                        onClick={() => setMenuAberto(false)}
                                    >
                                        <span className={styles.overlayNum} aria-hidden="true">
                                            <FaHeart />
                                        </span>
                                        <span className={styles.overlayTexto}>Favoritos{totalFavoritos > 0 ? ` (${totalFavoritos})` : ''}</span>
                                    </Link>
                                </motion.div>
                            </motion.nav>

                            <motion.div
                                className={styles.overlayRodape}
                                initial={{ opacity: 0, y: reduzido ? 0 : 20 }}
                                animate={{ opacity: 1, y: 0, transition: { delay: reduzido ? 0 : 0.35, duration: 0.5, ease: EASE_HORIZONTE } }}
                                exit={{ opacity: 0, transition: { duration: 0.15 } }}
                            >
                                {phones.length > 0 ? (
                                    <div className={styles.overlayContatos}>
                                        {phones.map((p, idx) => (
                                            <a
                                                key={idx}
                                                className={styles.overlayContato}
                                                href={p.whats ? linkWhatsAppDoTenant(site, p.tel) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`}
                                                target={p.whats ? '_blank' : undefined}
                                                rel="noopener noreferrer"
                                                onClick={() => {
                                                    trackLead(site, null); // whatsapp_click (GTM/GA), como no menu do Topo clássico
                                                    trackMetaEvent(site, 'Contact', {
                                                        content_name: 'Contato do menu',
                                                        contact_method: p.whats ? 'whatsapp' : 'phone',
                                                        content_category: 'header',
                                                    });
                                                }}
                                            >
                                                {p.whats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                                {MaskPhone(p.tel || '')}
                                            </a>
                                        ))}
                                    </div>
                                ) : null}
                                {redes.length > 0 ? (
                                    <div className={styles.overlayRedes}>
                                        {redes.map((r) => {
                                            const Icone = ICONE_REDE[r.rede];
                                            return (
                                                <a key={r.rede} href={r.href} target="_blank" rel="noopener noreferrer" className={styles.overlayRede} aria-label={r.rede} title={r.rede}>
                                                    {Icone ? <Icone aria-hidden={true} /> : r.rede}
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </div>
        </MotionConfig>
    );
};

// ErrorBoundary → fallback pro Topo padrão (mesmo padrão do TopoAurora/TopoAutoral).
class TopoHorizonte extends React.Component<ITopoHorizonte, { hasError: boolean }> {
    constructor(props: ITopoHorizonte) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: o topo degrada pro padrão sem quebrar a página.
    }
    render() {
        const { site, setActiveDrawer, fallback } = this.props;
        const defaultTopo = fallback ?? <Topo site={site} setActiveDrawer={setActiveDrawer} />;
        if (this.state.hasError || !site) return defaultTopo;
        return <TopoHorizonteInner {...this.props} />;
    }
}

export default TopoHorizonte;
